import { memo } from 'react';

import cn from 'classnames';
import { useHistory } from 'react-router-dom';

import { changeAppDrawerTab } from 'actions/appDrawer';
import StripPortal from 'components/StripPortal';
import { STORAGE_KEYS } from 'constants/enums';
import { MIXPANEL_EVENT_NAMES } from 'constants/mixpanelEventNames';
import { routes } from 'constants/routes';
import { formatTimeInMinsOrHrs } from 'helpers/day';
import { trackMixpanelEvent } from 'helpers/mixpanel-init';
import { noop } from 'helpers/utils';
import useLocalStorage from 'hooks/useLocalStorage';
import i18n from 'i18n/config';
import { APP_DRAWER_TAB_ID_MAPPING } from 'reducers/initialstate/appDrawer';
import {
  scalperOfferMetadataSelector,
  scalpingDataSelector,
} from 'selectors/otherSelectors';
import { useAppDispatch, useAppSelector } from 'storeHooks';
import { CloseIcon, ExternalLink } from 'styles/assets/icons';
import BonusTick from 'styles/assets/icons/BonusTick';
import Box from 'UIKit/Box';
import IconButton from 'UIKit/IconButton';

import styles from './styles.module.scss';

const getStaticScalperStripText = ({ futuresScalperTTL, optionsScalperTTL }) => {
  return i18n.t('common:scalperOfferStrip', { futuresScalperTTL, optionsScalperTTL });
};

const FUTURES_CONTRACT_ROUTE = `${routes.futures.trade}/BTC/BTCUSD?showScalpingPopup=true`;

const ScalperOfferStrip = memo(() => {
  const history = useHistory();
  const appDispatch = useAppDispatch();

  const [scalperOfferStripShown, setScalperOfferStripShown] = useLocalStorage(
    STORAGE_KEYS.SCALPER_OFFER_STRIP_SHOWN_KEY,
    true
  );

  const { scalperOffer } = useAppSelector(scalpingDataSelector);
  const { scalperOfferTimeRange = 0, optionsScalperOfferTimeRange = 0 } = useAppSelector(
    scalperOfferMetadataSelector
  );
  const isScalpingEnabledForUser = scalperOffer?.futures_scalper_offer_enabled;

  const showStrip = scalperOfferStripShown && !isScalpingEnabledForUser;

  const onCloseClickHandler = () => {
    setScalperOfferStripShown(false);
    trackMixpanelEvent(MIXPANEL_EVENT_NAMES.STRIP_CLICKED, {
      strip_name: 'Scalper Offer',
    });
  };

  const onClickHandler = () => {
    appDispatch(changeAppDrawerTab(APP_DRAWER_TAB_ID_MAPPING.TRADE));

    history.push(FUTURES_CONTRACT_ROUTE);
    trackMixpanelEvent(MIXPANEL_EVENT_NAMES.STRIP_CLICKED, {
      strip_name: 'Scalper Offer',
    });
  };

  const futuresScalperTTL = formatTimeInMinsOrHrs({
    seconds: scalperOfferTimeRange,
    format: 'short',
  });
  const optionsScalperTTL = formatTimeInMinsOrHrs({
    seconds: optionsScalperOfferTimeRange,
    format: 'short',
  });
  const scalperOfferStripText = getStaticScalperStripText({
    futuresScalperTTL,
    optionsScalperTTL,
  });

  return (
    <StripPortal isSticky={false}>
      <Box className={cn(styles.stripContainer)} flex="flex" renderWhen={showStrip}>
        <BonusTick
          className={styles.stripIcon}
          color="var(--white)"
          height={18}
          width={18}
        />

        <Box
          data-testid="scalper-offer-strip-text"
          as="span"
          className={cn(styles.informationText)}
          onClick={onClickHandler}
          rel="noopener noreferrer"
        >
          {scalperOfferStripText}
          <ExternalLink className={styles.linkIcon} />
        </Box>
        <IconButton
          className={cn(styles.closeIcon)}
          data-testid="scalper-offer-strip-close-icon"
          onClick={onCloseClickHandler}
          onKeyDown={noop}
          icon={<CloseIcon color="currentColor" />}
        />
      </Box>
    </StripPortal>
  );
});

ScalperOfferStrip.displayName = 'InformationStrip';

export { ScalperOfferStrip };
